import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlineCheckCircle, AiOutlineCloseCircle, AiOutlineRotateLeft } from "react-icons/ai";
import { VscServerProcess } from "react-icons/vsc";
import { FiChevronLeft as ChevronLeft, FiChevronRight as ChevronRight } from "react-icons/fi";
import { Modal, Badge, Table } from 'react-bootstrap';
import Loader from '../../component/loader';
import axios from 'axios';
import Cookies from 'js-cookie';
import { decrpt } from '../../component/encryption';

function OrderList() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [loader, setLoader] = useState(false);
  const [orderList, setOrderList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);

  // Pagination & Search states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");

  const fetchOrderList = async () => {
    setLoader(true);
    try {
      let token = decrpt(Cookies.get('s_l'));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.post('/seller/orders/list', {});
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setOrderList(response.data.data);
      } else {
        setOrderList([]);
      }
    } catch (error) {
      console.log('Error', error);
      setOrderList([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchOrderList();
  }, []);

  const statusHandler = (status) => {
    setSelectedStatus((prev) => (prev === status ? null : status));
    setCurrentPage(1);
  };

  const orderStatusModalClose = () => {
    setShow(false);
  };

  const orderStatusChange = (item, status) => {
    setOrderStatus({ item, status });
    setShow(true);
  };

  const statusChange = async () => {
    if (!orderStatus || !orderStatus.item) return;
    setLoader(true);
    setShow(false);
    const data = {
      order_id: orderStatus.item.order_id,
      status: orderStatus.status
    };
    try {
      let token = decrpt(Cookies.get('s_l'));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await axios.post(`/seller/orders/status/update`, data);
      fetchOrderList();
    } catch (error) {
      console.log(error);
      setLoader(false);
    }
  };

  const viewDetails = (orderId) => {
    navigate('/order/single', { state: { item: orderId } });
  };

  // Filtered orders by status & search
  const filteredOrders = useMemo(() => {
    if (!Array.isArray(orderList)) return [];
    let list = orderList;

    if (selectedStatus !== null && selectedStatus !== undefined) {
      list = list.filter((item) => Number(item.order_status) === Number(selectedStatus));
    }

    if (!searchText.trim()) return list;

    const q = searchText.trim().toLowerCase();
    return list.filter((order) => {
      const uniqueId = String(order.unique_id || "").toLowerCase();
      const productName = String(order.product_name || "").toLowerCase();
      const brand = String(order.brand || "").toLowerCase();
      const id = String(order.id || "").toLowerCase();
      const orderId = String(order.order_id || "").toLowerCase();
      const paymentType = String(order.payment_type || "").toLowerCase();
      return (
        uniqueId.includes(q) ||
        productName.includes(q) ||
        brand.includes(q) ||
        id.includes(q) ||
        orderId.includes(q) ||
        paymentType.includes(q)
      );
    });
  }, [orderList, selectedStatus, searchText]);

  // Pagination logic
  const totalItems = filteredOrders.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredOrders.slice(startIndex, endIndex);
  }, [filteredOrders, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="main-content">
      <div className="count-section d-flex justify-content-between mb-3 flex-wrap gap-2">
        <button
          className="order-page-block d-flex align-items-center"
          onClick={() => statusHandler(1)}
          style={selectedStatus === 1 ? styles.activeBtn : null}
        >
          <div className="order-page-inner-block">
            <h5 style={selectedStatus === 1 ? styles.activeBtnTxt : null}>
              <VscServerProcess /> New Order
            </h5>
          </div>
        </button>

        <button
          className="order-page-block d-flex align-items-center"
          onClick={() => statusHandler(2)}
          style={selectedStatus === 2 ? styles.activeBtn : null}
        >
          <div className="order-page-inner-block">
            <h5 style={selectedStatus === 2 ? styles.activeBtnTxt : null}>
              <AiOutlineCheckCircle /> Accepted Order
            </h5>
          </div>
        </button>

        <button
          className="order-page-block d-flex align-items-center"
          onClick={() => statusHandler(3)}
          style={selectedStatus === 3 ? styles.activeBtn : null}
        >
          <div className="order-page-inner-block">
            <h5 style={selectedStatus === 3 ? styles.activeBtnTxt : null}>
              <AiOutlineRotateLeft /> Dispatched Order
            </h5>
          </div>
        </button>

        <button
          className="order-page-block d-flex align-items-center"
          onClick={() => statusHandler(4)}
          style={selectedStatus === 4 ? styles.activeBtn : null}
        >
          <div className="order-page-inner-block">
            <h5 style={selectedStatus === 4 ? styles.activeBtnTxt : null}>
              <AiOutlineCloseCircle /> Out For Delivery
            </h5>
          </div>
        </button>

        <button
          className="order-page-block d-flex align-items-center"
          onClick={() => statusHandler(5)}
          style={selectedStatus === 5 ? styles.activeBtn : null}
        >
          <div className="order-page-inner-block">
            <h5 style={selectedStatus === 5 ? styles.activeBtnTxt : null}>
              <AiOutlineCheckCircle /> Delivered Order
            </h5>
          </div>
        </button>

        <button
          className="order-page-block d-flex align-items-center"
          onClick={() => statusHandler(6)}
          style={selectedStatus === 6 ? styles.activeBtn : null}
        >
          <div className="order-page-inner-block">
            <h5 style={selectedStatus === 6 ? styles.activeBtnTxt : null}>
              <AiOutlineRotateLeft /> Cancelled By Seller
            </h5>
          </div>
        </button>

        <button
          className="order-page-block d-flex align-items-center"
          onClick={() => statusHandler(7)}
          style={selectedStatus === 7 ? styles.activeBtn : null}
        >
          <div className="order-page-inner-block">
            <h5 style={selectedStatus === 7 ? styles.activeBtnTxt : null}>
              <AiOutlineRotateLeft /> Cancelled By User
            </h5>
          </div>
        </button>

        <button
          className="order-page-block d-flex align-items-center"
          onClick={() => statusHandler(8)}
          style={selectedStatus === 8 ? styles.activeBtn : null}
        >
          <div className="order-page-inner-block">
            <h5 style={selectedStatus === 8 ? styles.activeBtnTxt : null}>
              <AiOutlineRotateLeft /> Cancelled By Admin
            </h5>
          </div>
        </button>
      </div>

      {loader ? (
        <Loader />
      ) : (
        <div className="section">
          <div className="row">
            <div className="col-md-12">
              <div className="card mb-3 p-2">
                <div className="card-title d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                  <h5 className="mb-0">All Order Items</h5>
                  <div style={{ maxWidth: '300px', width: '100%' }}>
                    <input
                      type="text"
                      placeholder="Search by Order ID, Product, Brand..."
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="form-control form-control-sm"
                      style={{ border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div>
                  <Table responsive="sm" className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Product Name</th>
                        <th>Brand Name</th>
                        <th className="text-center">Date of Order</th>
                        <th className="text-center">Quantity</th>
                        <th className="text-center">Order Amount</th>
                        <th className="text-center">Payment Type</th>
                        <th className="text-center">Order Status</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedOrders && paginatedOrders.length > 0 ? (
                        paginatedOrders.map((item, index) => {
                          return (
                            <tr key={item.id || index}>
                              <td>{item.unique_id}</td>
                              <td>
                                {item.product_image && (
                                  <img
                                    src={item.product_image}
                                    alt=""
                                    className="product-table-img me-2"
                                    style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                                  />
                                )}
                                {item.product_name}
                              </td>
                              <td>{item.brand || '—'}</td>
                              <td className="text-center">{item.order_date}</td>
                              <td className="text-center">{item.quantity}</td>
                              <td className="text-center">
                                <div className="fw-bold text-success" style={{ fontSize: '14px' }}>
                                  ₹ {Number(
                                    item.overall_price ??
                                    item.order_amount ??
                                    (item.total_amount ? Number(item.total_amount) + Number(item.product_gst_amount || 0) : null) ??
                                    item.price ??
                                    0
                                  ).toFixed(2)}
                                </div>
                              </td>
                              <td className="text-center">{item.payment_type || '—'}</td>
                              <td className="text-center">
                                {item.order_status == 1 ? <Badge bg="primary">New</Badge> :
                                 item.order_status == 2 ? <Badge bg="success">Accepted</Badge> :
                                 item.order_status == 3 ? <Badge bg="warning" text="dark">Dispatch</Badge> :
                                 item.order_status == 4 ? <Badge bg="secondary">Out For Delivery</Badge> :
                                 item.order_status == 5 ? <Badge bg="success">Delivered</Badge> :
                                 item.order_status == 6 ? <Badge bg="danger">Cancelled By Seller</Badge> :
                                 item.order_status == 7 ? <Badge bg="danger">Cancelled By User</Badge> :
                                 <Badge bg="danger">Cancelled By Admin</Badge>}
                              </td>
                              <td className="text-center">
                                <button
                                  type="button"
                                  className="btn btn-xs btn-primary text-white me-1"
                                  onClick={() => viewDetails(item.order_id)}
                                >
                                  View
                                </button>
                                {item.order_status == 1 && (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-xs btn-success text-white me-1"
                                      onClick={() => orderStatusChange(item, 2)}
                                    >
                                      Accept
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-xs btn-danger text-white"
                                      onClick={() => orderStatusChange(item, 7)}
                                    >
                                      Deny
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={9} className="text-center py-4 text-muted">No data found</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {/* Pagination Controls */}
                  {totalItems > 0 && (
                    <div
                      className="flex items-center justify-between mt-4 px-2"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: '1rem',
                        padding: '0 0.5rem',
                        flexWrap: 'wrap',
                        gap: '0.75rem'
                      }}
                    >
                      <div
                        className="flex items-center gap-2"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                      >
                        <span className="text-sm text-gray-700" style={{ fontSize: '0.875rem', color: '#374151' }}>
                          Show
                        </span>
                        <select
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                          className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black"
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            outline: 'none',
                            background: '#fff'
                          }}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>
                        <span className="text-sm text-gray-700" style={{ fontSize: '0.875rem', color: '#374151' }}>
                          entries
                        </span>
                      </div>

                      <div
                        className="flex items-center gap-2"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                      >
                        <button
                          type="button"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-black font-medium"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            background: '#fff',
                            color: '#000',
                            opacity: currentPage === 1 ? 0.5 : 1,
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <ChevronLeft className="h-4 w-4" style={{ height: '1rem', width: '1rem' }} /> Previous
                        </button>

                        <span
                          className="text-sm font-medium px-2 text-gray-700"
                          style={{ fontSize: '0.875rem', fontWeight: 500, padding: '0 0.5rem', color: '#374151' }}
                        >
                          Page {currentPage} of {totalPages}
                        </span>

                        <button
                          type="button"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-black font-medium"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            background: '#fff',
                            color: '#000',
                            opacity: currentPage === totalPages ? 0.5 : 1,
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Next <ChevronRight className="h-4 w-4" style={{ height: '1rem', width: '1rem' }} />
                        </button>
                      </div>

                      <div className="text-sm text-gray-700" style={{ fontSize: '0.875rem', color: '#374151' }}>
                        Showing {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{' '}
                        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* approve reject modal */}
      <Modal size="sm" show={show} onHide={orderStatusModalClose} centered>
        <Modal.Header>
          {orderStatus && orderStatus.status == '2' ? (
            <Modal.Title className="h6 fw-bolder">Are You Sure to Accept?</Modal.Title>
          ) : orderStatus && orderStatus.status == '3' ? (
            <Modal.Title className="h6 fw-bolder">Are You Sure to Dispatched?</Modal.Title>
          ) : orderStatus && orderStatus.status == '4' ? (
            <Modal.Title className="h6 fw-bolder">Are You Sure to Out For Delivery?</Modal.Title>
          ) : orderStatus && orderStatus.status == '5' ? (
            <Modal.Title className="h6 fw-bolder">Are You Sure to Delivered?</Modal.Title>
          ) : (
            <Modal.Title className="h6 fw-bolder">Are You Sure to Deny?</Modal.Title>
          )}
        </Modal.Header>
        <Modal.Footer className="p-1 justify-content-end" style={{ borderTop: 'none' }}>
          <button onClick={() => orderStatusModalClose()} className="btn btn-light btn-sm">
            No
          </button>
          <button onClick={() => statusChange()} className="btn btn-primary btn-sm">
            Yes
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default OrderList;

const styles = {
  activeBtn: { backgroundColor: '#ffc001ad' },
  activeBtnTxt: { color: '#000' }
};
